"use client";

import { FC, useMemo } from "react";
import React from "react";
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from "react-google-recaptcha-v3";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import Joi from "joi";
import jwt from "jsonwebtoken";
import dayjs from "dayjs";
import { signIn } from "next-auth/react";
import { setCookie } from "nookies";
import { encrypt } from "~libs/encrypt";
import { axiosInstance } from "~libs/axios-instance";
import HttpErrorHandler from "~components/http-error-handler";
import { Button } from "~components/button";
import { Input } from "~components/input";
import { useTranslation } from "react-i18next";
import { useAgent } from "~context/agent";
import { objectify } from "radash";

interface UpdateSignInFormProps {
  username: string;
  password: string;
}

const LoginClientPage: FC<{}> = () => {
  const { t } = useTranslation(["common"]);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { agentInfo } = useAgent();

  const agentLogo = useMemo(() => {
    const images = objectify(
      agentInfo?.images || [],
      (k) => k.name,
      (v) => v.value,
    );
    return images.horizontalLogo;
  }, [agentInfo]);

  const schemaErrorsSignInForm = Joi.object({
    username: Joi.string()
      .required()
      .messages({
        "any.required": t("common:error-username"),
        "string.empty": t("common:error-username"),
      }),
    password: Joi.string().messages({
      "any.required": t("common:error-password"),
      "string.empty": t("common:error-password"),
    }),
  }).unknown(true);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<UpdateSignInFormProps>({
    resolver: joiResolver(schemaErrorsSignInForm),
  });

  const handleRequestLogin: SubmitHandler<UpdateSignInFormProps> = async (
    values,
  ) => {
    let response;
    const { username, password } = values;

    try {
      if (!executeRecaptcha) {
        return;
      }
      const token = await executeRecaptcha("login");
      const encryptPassword = encrypt(password);
      response = await axiosInstance.post("/authentication/login", {
        username: username.trim().toLowerCase(),
        password: encryptPassword,
        token,
      });
    } catch (error) {
      HttpErrorHandler(error);
    }
    if (response) {
      const jwtDecoded = jwt.decode(response.data.accessToken) as {
        exp: number;
      };
      setCookie(undefined, "access_token", response.data.accessToken, {
        maxAge: dayjs(jwtDecoded.exp * 1000).diff(dayjs(), "s"),
        path: "/",
      });
      await signIn("credentials", {
        accessToken: response.data.accessToken,
      });
    }
  };

  return (
      <div className="flex items-center justify-center h-full px-4 sm:px-0">
        <form
            className="relative z-10 flex w-full flex-col gap-3"
            onSubmit={handleSubmit(handleRequestLogin)}
        >
          <Input
              {...register("username")}
              errors={
                errors.username?.message ? [errors.username.message] : undefined
              }
              placeholder={t("common:username")}
          />
          <Input
              {...register("password")}
              errors={
                errors.password?.message ? [errors.password.message] : undefined
              }
              placeholder={t("common:password")}
              type="password"
          />
          <Button loading={isSubmitting} type="submit" className="mt-4">
            {t("common:login")}
          </Button>
        </form>
      </div>
  );
};

const PageWithGoogleRecaptcha = (): React.JSX.Element => {
  return (
      <GoogleReCaptchaProvider
          reCaptchaKey={process.env.GOOGLE_RECAPTCHA_SITEKEY!}
      >
        <LoginClientPage/>
      </GoogleReCaptchaProvider>
  );
};

export default PageWithGoogleRecaptcha;
